import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

import { ConnectorConfiguration, GoogleServiceType } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import {
  ConnectorForm,
  GoogleAnalyticsConfiguration,
} from './google-analytics-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-google-analytics-connector-dialog',
  templateUrl: './google-analytics-connector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    NgTemplateOutlet,
    ButtonComponent,
    IconsModule,
    ReactiveFormsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class GoogleAnalyticsConnectorDialogComponent
  extends BaseConnectorDialogComponent<GoogleAnalyticsConfiguration>
  implements OnInit
{
  private panelRef = inject(PanelRef);

  serviceName = 'google';
  connectorForm: FormGroup<ConnectorForm>;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      propertyId: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.withOAuth();
  }

  ngOnInit(): void {
    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);
    this.goToAuth({ reauthorize: true });
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): GoogleAnalyticsConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const authState = this.authState ?? String(connector?.configuration?.authState ?? '');
    const propertyId = String(connector?.configuration?.propertyId ?? '');

    return { connectorId, connectorName, authState, propertyId };
  }

  fillForm(configuration: GoogleAnalyticsConfiguration): void {
    const { connectorId, connectorName, authState, propertyId } = configuration;

    this.connectorId = connectorId;
    this.authState = authState;

    this.connectorForm.patchValue(
      {
        connectorName,
        propertyId,
      },
      { emitEvent: false },
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, propertyId } = this.connectorForm.value;

    if (!connectorName || !propertyId) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
        propertyId,
      },
    );

    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
      });
  }

  protected override getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName, {
      googleServiceType: GoogleServiceType.Analytics,
    });
  }
}
